import { DateFormatter } from '@repo/toolkit/date'
import { useNavigator } from '@repo/toolkit/use-navigator'
import Box from '@repo/ui/box'
import CapsuleTab from '@repo/ui/capsule-tab'
import { ChevronLeftIcon } from '@repo/ui/icons'
import { Skeleton } from '@repo/ui/skeleton'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { Route, Routes, useParams } from 'react-router-dom'

import { getPeriodByAvailability } from '../../api/periods/get-period-by-availability'
import { getTripFolder } from '../../api/trip-folder/get-trip-folder-details'
import { ItemNotFound } from '../../components/elements/item-not-found'
import UserNavBar from '../../components/elements/nav-bar'
import PeriodBadge from '../../components/elements/period-badge'
import TripFolderLock from '../../components/elements/trip-folder-lock'
import { Paths } from '../../lib/constants/path'
import { useNavigateSections } from '../../lib/hooks/use-navigate-sections'
import { tripStepTypeMapping } from '../../lib/types/enums/trip/trip-type.enum'
import { TripProperties } from '../../lib/types/models/trip/properties.trip'
import { TripFolderProperties } from '../../lib/types/models/trip-folder/trip-folder-properties'
import { filterDuplicates } from '../../lib/utils/functions/filter-duplicates'
import { getTripFolderLabel } from '../../lib/utils/functions/trip-folder.functions'
import NotFoundPage from '../common/not-found'
import FolderDetails from './trip-folder-details/folder-details'
import FolderDocuments from './trip-folder-details/folder-documents'
import FolderTripBookingDocuments from './trip-folder-details/folder-trip-booking-documents'
import TripFolderPassengerDetails from './trip-folder-details/passenger-details'

const TripFolderDetailsPage = () => {
  const { id } = useParams<{ id: string }>()
  const { back } = useNavigator()

  const sections = [
    { label: 'Dossier', path: '' },
    { label: `Liste des pax`, path: Paths.TRIP_FOLDER_PASSENGER_DETAILS },
    { label: 'Documents annexes', path: Paths.TRIP_FOLDER_DOCUMENTS_PAGE },
    { label: 'Billets', path: Paths.TRIP_FOLDER_TICKETS_PAGE }
  ]

  const { activeTab, handleTabChange } = useNavigateSections({
    sections
  })

  const {
    data: tripFolder,
    isPending: isTripFolderPending,
    refetch
  } = useQuery({
    queryKey: ['trip-folder', id],
    queryFn: () => getTripFolder(+id!),
    enabled: !!id && !isNaN(+id)
  })

  const tripLabels = filterDuplicates(
    tripFolder?.tripBookings ?? [],
    (data) => TripFolderProperties.getTrip(data).id
  )

  const startDate = new Date(tripFolder?.date ?? '')

  const { data, isPending } = useQuery({
    queryKey: ['trip-date-period', startDate],
    queryFn: () => {
      const formattedStartDate = DateFormatter.toDateFormatted(startDate)

      return getPeriodByAvailability({
        startDate: formattedStartDate,
        endDate: formattedStartDate
      })
    },
    enabled: !!startDate
  })

  if (!id || isNaN(+id)) return <ItemNotFound title="Identifiant de dossier invalide" />
  if (!isTripFolderPending && !tripFolder) return <NotFoundPage />

  return (
    <div>
      <UserNavBar
        title={
          isTripFolderPending ? (
            <Skeleton className="w-64 h-6" />
          ) : (
            <div className="flex flex-wrap gap-2 max-w-1/2">
              {tripLabels.map((data, index) => (
                <Typography variant="h4" key={TripFolderProperties.getTrip(data).id}>
                  {TripProperties.getTripLabel(TripFolderProperties.getTrip(data))}
                  {index !== tripLabels.length - 1 && <span className="ml-2">|</span>}
                </Typography>
              ))}
              {tripFolder?.directionality && (
                <Typography
                  variant="h4"
                  color={tripStepTypeMapping[tripFolder?.directionality].textColor}
                >
                  {tripStepTypeMapping[tripFolder?.directionality].label}
                </Typography>
              )}
              {tripFolder?.date && startDate && (
                <PeriodBadge
                  variant="fushia"
                  period={data}
                  dateDisplay={startDate}
                  loading={isPending}
                />
              )}
              <TripFolderLock
                id={+id}
                label={tripFolder ? getTripFolderLabel(tripFolder) : ''}
                locked={tripFolder?.locked ?? false}
                onSuccess={() => refetch()}
              />
            </div>
          )
        }
        icon={ChevronLeftIcon}
        onIconClick={() => back(Paths.TRIP_FOLDER_LIST_PAGE)}
      />
      <div className={'h-4'} />
      <Box>
        <div className="sticky top-0 py-3 z-10 bg-white shadow-sm">
          <CapsuleTab
            variant="secondary"
            activeTab={activeTab}
            onTabChange={handleTabChange}
            tabs={sections.map(({ label }) => ({ label }))}
          />
        </div>

        <Routes>
          <Route path="" element={<FolderDetails folderId={+id} />} />
          <Route
            path={Paths.TRIP_FOLDER_DOCUMENTS_PAGE}
            element={<FolderDocuments folderId={+id} />}
          />
          <Route
            path={Paths.TRIP_FOLDER_TICKETS_PAGE}
            element={<FolderTripBookingDocuments folderId={+id} />}
          />

          <Route
            path={Paths.TRIP_FOLDER_PASSENGER_DETAILS}
            element={<TripFolderPassengerDetails folderId={+id} />}
          />
        </Routes>
      </Box>
    </div>
  )
}

export default TripFolderDetailsPage
